import { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DailyCount } from "../../api/types";
import type { StatusTone } from "../../lib/sim";
import { FilterIcon } from "../icons";
import { Empty } from "../ui";

// Redesign building blocks shared by the simulated-patient and simulated-
// attempt pages. The table / search / stat primitives come from
// ../courses/ui (the admin redesign library); these are the sim-specific
// pieces: the filter button, pill tabs, status dots and the activity chart.

export function FilterButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-label="Filters"
      aria-expanded={open}
      onClick={onToggle}
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#0B5785] text-white shadow-sm transition-colors hover:bg-[#0a4a71]"
    >
      <FilterIcon className="h-6 w-6" />
    </button>
  );
}

export interface Tab<T extends string> {
  key: T;
  label: string;
  count?: number;
}

// Rounded pill tabs with an optional count badge (the red badges in the design).
export function PillTabs<T extends string>({ tabs, value, onChange }: {
  tabs: Tab<T>[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {tabs.map((t) => {
        const active = t.key === value;
        return (
          <button
            key={t.key}
            type="button"
            aria-current={active ? "page" : undefined}
            onClick={() => onChange(t.key)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              active ? "bg-[#0B5785] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {t.label}
            {t.count !== undefined && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold leading-none text-white">
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export type Tone = StatusTone;

const DOT: Record<StatusTone, string> = { green: "bg-green-500", amber: "bg-amber-500", gray: "bg-gray-400" };
const TEXT: Record<StatusTone, string> = { green: "text-green-600", amber: "text-amber-600", gray: "text-gray-500" };

// Coloured dot + label, as used for attempt and scenario statuses.
export function DotStatus({ tone, label }: { tone: StatusTone; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${TEXT[tone]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${DOT[tone]}`} />
      {label}
    </span>
  );
}

// --- activity chart ---------------------------------------------------------

type Period = "daily" | "weekly" | "monthly";

const PERIODS: { value: Period; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

interface Bucket {
  label: string;
  started: number;
  completed: number;
  sort: number;
}

// Roll the daily counts up into the chosen period. The API always returns
// per-day rows; weekly buckets start on Monday.
function toBuckets(daily: DailyCount[], period: Period): Bucket[] {
  const buckets = new Map<string, Bucket>();
  for (const d of daily) {
    const date = new Date(`${d.date}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) continue;
    let key: string;
    let label: string;
    let sort: number;
    if (period === "daily") {
      key = d.date;
      label = date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      sort = date.getTime();
    } else if (period === "weekly") {
      const monday = new Date(date);
      monday.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
      key = monday.toISOString().slice(0, 10);
      label = monday.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      sort = monday.getTime();
    } else {
      key = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
      label = date.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
      sort = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1);
    }
    const bucket = buckets.get(key) ?? { label, started: 0, completed: 0, sort };
    bucket.started += d.started;
    bucket.completed += d.completed;
    buckets.set(key, bucket);
  }
  return [...buckets.values()].sort((a, b) => a.sort - b.sort);
}

interface ActivityTooltipEntry {
  dataKey?: string | number;
  value?: number;
}

function ActivityTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: ActivityTooltipEntry[];
  label?: string | number;
}) {
  if (!active || !payload?.length) return null;
  const started = payload.find((p) => p.dataKey === "started")?.value ?? 0;
  const completed = payload.find((p) => p.dataKey === "completed")?.value ?? 0;
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="font-medium text-gray-800">{label}</div>
      <div className="mt-1 flex items-center gap-2 text-gray-700">
        <span className="h-2 w-2 rounded-full bg-gray-300" />
        {started.toLocaleString()} started
      </div>
      <div className="flex items-center gap-2 text-gray-700">
        <span className="h-2 w-2 rounded-full bg-[#0B5785]" />
        {completed.toLocaleString()} completed
      </div>
    </div>
  );
}

// "Activity" panel: started attempts as bars, completed as a line, with the
// busiest bucket highlighted. A period selector switches day/week/month.
export function ActivityPanel({ daily }: { daily: DailyCount[] }) {
  const [period, setPeriod] = useState<Period>("weekly");
  const data = useMemo(() => toBuckets(daily, period), [daily, period]);
  const hasData = daily.some((d) => d.started || d.completed);
  const peak = data.reduce((best, d, i, arr) => (d.started > arr[best].started ? i : best), 0);

  return (
    <section className="flex h-full flex-col rounded-xl border border-gray-100 bg-white p-5 shadow-[0_1px_3px_rgba(16,24,40,0.06)]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-[15px] font-semibold text-gray-900">Activity</h2>
        <select
          aria-label="Activity period"
          value={period}
          onChange={(e) => setPeriod(e.target.value as Period)}
          className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 focus:border-[#0B5785] focus:outline-none"
        >
          {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </div>
      {!hasData ? (
        <div className="flex flex-1 items-center justify-center"><Empty>No attempts recorded yet.</Empty></div>
      ) : (
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -18 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={{ stroke: "#e5e7eb" }} minTickGap={16} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} axisLine={false} width={40} />
              <Tooltip content={<ActivityTooltip />} cursor={{ fill: "rgba(11,87,133,0.05)" }} />
              <Bar dataKey="started" radius={[3, 3, 0, 0]} maxBarSize={24} isAnimationActive={false}>
                {data.map((_, i) => <Cell key={i} fill={i === peak ? "#F59E0B" : "#E5E7EB"} />)}
              </Bar>
              <Line
                type="monotone"
                dataKey="completed"
                stroke="#0B5785"
                strokeWidth={2}
                dot={{ r: 3, fill: "#0B5785" }}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
